import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import { getLocales } from 'assets/locales/Locale';
import { normalizeCountryCode } from 'assets/utils/helpers/NormalizationHelper';
import { flatMap, groupBy, isEmpty, keys, uniq } from 'lodash';
import ClaimJob from 'models/claims/claimJob/Model.ClaimJob';
import ClaimJobItem from 'models/claims/claimJobItem/Model.ClaimJobItem';
import ClaimJobItemPayee from 'models/claims/claimJobItemPayee/Model.ClaimJobItemPayee';
import payeeListApi from 'models/claims/payee/list/Api.Payee.List';
import { CountryCode } from 'models/enums/CountryCode';
import Currency from 'models/enums/Currency';
import LineItem from 'models/enums/LineItem';
import PaymentStatus from 'models/enums/PaymentStatus';
import Insurer from 'models/productManagement/insurer/Model.Insurer';
import { useEffect, useMemo } from 'react';
import { useClaimJobEdit } from '../ClaimEdit.Job.Hooks';
import ClaimEditJobSubmitButton from '../ClaimEdit.Job.SubmitButton';
import ClaimEditJobAggregateTable from '../claimJobAggregates/ClaimEdit.Job.AggregateTable';
import ClaimEditJobDeductibleTable from './tables/ClaimEdit.Job.Deductible.Table';
import ClaimEditJobLabourTable from './tables/ClaimEdit.Job.Labour.Table';
import ClaimEditJobOtherTable from './tables/ClaimEdit.Job.Other.Table';
import ClaimEditJobPartTable from './tables/ClaimEdit.Job.Part.Table';
import Box from 'assets/components/box/Box';
import ThemeWrapperStack from 'assets/themes/wrappers/Theme.Wrapper.Stack';

export default function ClaimEditJobItems() {
  const { lang } = getLocales();
  const {
    form,
    claimType,
    updateJobItem,
    removeJobItem,
    updateMultipleJobItems,
    getJobItemDefaultValues,
    isWarrantyProduct,
    isClaimJobEditableByStatus,
    permissions,
    isClaimAdmin,
    newPayeeId,
    isClaimOwner,
    product,
    setOverrideLabour,
    overrideLabour,
  } = useClaimJobEdit();

  const aggregates = useMemo(() => ClaimJob.aggregates(new ClaimJob(form.data)), [form.data]);
  const itemMap = useMemo(
    () =>
      groupBy(form?.data?.claimJobItems, (it) => {
        const countryCode =
          it.claimJobItemPayee?.payee?.countryCode ??
          it.claimJobItemPayee?.client?.countryCode ??
          it.claimJobItemPayee?.dealer?.countryCode ??
          CountryCode.CAN;

        return normalizeCountryCode(countryCode);
      }),
    [form.data.claimJobItems]
  );

  useEffect(() => {
    (async () => {
      if (newPayeeId) {
        const payeeList = await payeeListApi();
        const newPayee = payeeList?.payload?.data?.find((it) => it.id === newPayeeId);
        if (newPayee) {
          const tempClaimJobItems = form?.data?.claimJobItems || [];
          const claimJobItemPayee = new ClaimJobItemPayee({
            payee: newPayee,
            payeeId: newPayee.id,
            payeeType: newPayee.payeeType,
          });

          const updatedClaimJobItems = tempClaimJobItems?.map((it) =>
            (it.claimJobItemPayee?.payeeId ?? it.claimJobItemPayee?.dealerId ?? it.claimJobItemPayee?.clientId)
              ? it
              : {
                  ...it,
                  claimJobItemPayee,
                  ...getJobItemDefaultValues({ jobItemTypeCode: it.jobItemTypeCode, claimJobItemPayee }),
                }
          );
          form.update({ claimJobItems: updatedClaimJobItems });
        }
      }
    })();
  }, [newPayeeId]);

  const isAddDisabled = (isClaimOwner && permissions?.CREATE_CLAIMJOBITEM) || isClaimAdmin;
  const insurerHeaders = useMemo(
    () =>
      uniq([
        ...flatMap(form.data.claimJobItems, (cji) => keys(cji.insurerAdjudications)),
        ...(product?.insurers?.map((insurer) => insurer.name) ?? []),
      ]),
    [form.data.claimJobItems, product?.insurers]
  );

  return (
    <>
      <h3>{lang.jobDetails}</h3>
      {!!itemMap[CountryCode.CAN]?.length && (
        <>
          <ClaimEditJobPartTable
            items={itemMap[CountryCode.CAN]
              .map((item, index) => ({
                item,
                index,
              }))
              .filter((it) => it.item?.jobItemTypeCode === LineItem.part)}
            isWarrantyProduct={isWarrantyProduct}
            isAddDisabled={isClaimJobEditableByStatus || !isAddDisabled}
            updateJobItem={updateJobItem}
            removeJobItem={removeJobItem}
            lossCodes={claimType?.lossCodes ?? []}
            currency={Currency.CAD}
            product={product}
            insurerHeaders={insurerHeaders}
          />
          <ClaimEditJobLabourTable
            items={itemMap[CountryCode.CAN]
              .map((item, index) => ({
                item,
                index,
              }))
              .filter((it) => it.item?.jobItemTypeCode === LineItem.labour)}
            isWarrantyProduct={isWarrantyProduct}
            isAddDisabled={isClaimJobEditableByStatus || !isAddDisabled}
            updateJobItem={updateJobItem}
            removeJobItem={removeJobItem}
            lossCodes={claimType?.lossCodes ?? []}
            setOverrideLabour={setOverrideLabour}
            overrideLabour={overrideLabour}
            currency={Currency.CAD}
            product={product}
            insurerHeaders={insurerHeaders}
          />
          {isWarrantyProduct && (
            <ClaimEditJobDeductibleTable
              items={itemMap[CountryCode.CAN]
                .map((item, index) => ({
                  item,
                  index,
                }))
                .filter((it) => it.item?.jobItemTypeCode === LineItem.deductible)}
              isWarrantyProduct={isWarrantyProduct}
              updateJobItem={updateJobItem}
              currency={Currency.CAD}
              insurerHeaders={insurerHeaders}
            />
          )}
          <ClaimEditJobOtherTable
            items={itemMap[CountryCode.CAN]
              .map((item, index) => ({
                item,
                index,
              }))
              .filter((it) => it.item?.jobItemTypeCode === LineItem.other)}
            isWarrantyProduct={isWarrantyProduct}
            isAddDisabled={isClaimJobEditableByStatus || !isAddDisabled}
            updateJobItem={updateJobItem}
            removeJobItem={removeJobItem}
            lossCodes={claimType?.lossCodes ?? []}
            currency={Currency.CAD}
            product={product}
            insurerHeaders={insurerHeaders}
          />
          &nbsp;
          <ClaimEditJobAggregateTable
            currency={Currency.CAD}
            aggregates={aggregates.filter((it) => it.currency === Currency.CAD)}
            onUpdate={({ claimJobItemPayee, ...rest }) => updateMultipleJobItems(claimJobItemPayee, rest)}
          />
        </>
      )}
      {!!itemMap[CountryCode.USA]?.length && !!itemMap[CountryCode.CAN]?.length ? <br /> : undefined}
      {!!itemMap[CountryCode.USA]?.length && (
        <>
          <h3>{CountryCode.USA}</h3>
          <ClaimEditJobPartTable
            items={itemMap[CountryCode.USA]
              .map((item, index) => ({
                item,
                index,
              }))
              .filter((it) => it.item?.jobItemTypeCode === LineItem.part)}
            updateJobItem={updateJobItem}
            removeJobItem={removeJobItem}
            isAddDisabled={isClaimJobEditableByStatus || !isAddDisabled}
            lossCodes={claimType?.lossCodes ?? []}
            currency={Currency.USD}
            product={product}
            insurerHeaders={insurerHeaders}
          />
          <ClaimEditJobLabourTable
            items={itemMap[CountryCode.USA]
              .map((item, index) => ({
                item,
                index,
              }))
              .filter((it) => it.item?.jobItemTypeCode === LineItem.labour)}
            updateJobItem={updateJobItem}
            removeJobItem={removeJobItem}
            isAddDisabled={isClaimJobEditableByStatus || !isAddDisabled}
            lossCodes={claimType?.lossCodes ?? []}
            setOverrideLabour={setOverrideLabour}
            overrideLabour={overrideLabour}
            currency={Currency.USD}
            product={product}
            insurerHeaders={insurerHeaders}
          />
          <ClaimEditJobOtherTable
            items={itemMap[CountryCode.USA]
              .map((item, index) => ({
                item,
                index,
              }))
              .filter((it) => it.item?.jobItemTypeCode === LineItem.other)}
            updateJobItem={updateJobItem}
            removeJobItem={removeJobItem}
            isAddDisabled={isClaimJobEditableByStatus || !isAddDisabled}
            lossCodes={claimType?.lossCodes ?? []}
            currency={Currency.USD}
            product={product}
            insurerHeaders={insurerHeaders}
          />
          &nbsp;
          <ClaimEditJobAggregateTable
            currency={Currency.USD}
            aggregates={aggregates.filter((it) => it.currency === Currency.USD)}
            onUpdate={({ claimJobItemPayee, ...rest }) => updateMultipleJobItems(claimJobItemPayee, rest)}
          />
        </>
      )}
      <br />
      <Box class={ThemeWrapperStack}>
        <Button
          class={ThemeButtonCircle}
          media="fas-plus"
          onClick={() =>
            updateJobItem(
              new ClaimJobItem({
                jobItemTypeCode: LineItem.other,
                statusCode: PaymentStatus.unpaid,
                currency: Currency.USD,
                insurerAdjudications: Insurer.createInsurerAdjudications(
                  product?.insurers?.map((it) => new Insurer(it)),
                  Currency.USD
                ),
                adjudication: 0.0,
                correction: 0.0,
                claimJobItemPayee: { payee: { countryCode: CountryCode.USA } } as Model.IClaimJobItemPayee,
              }).toJSON()
            )
          }
          render={`${lang.create} ${'USA'} table`}
          disabled={isClaimJobEditableByStatus || !isAddDisabled || !isEmpty(itemMap[CountryCode.USA])}
        />
        <ClaimEditJobSubmitButton />
      </Box>
      <br />
    </>
  );
}
